import React, { useRef, useEffect, useState, useCallback } from 'react';
import '@progress/kendo-theme-default/dist/all.css';
import { Calendar } from '@progress/kendo-react-dateinputs';
import type { CalendarChangeEvent } from '@progress/kendo-react-dateinputs';
import { Popup } from '@progress/kendo-react-popup';
import { addDays } from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { ScheduleProvider, useSchedule } from '../context/ScheduleContext';
import { SchedulerView } from './scheduler/KendoSchedulerView';
import { ResourcePanel } from './ResourcePanel';
import { RightPanel } from './RightPanel';
import { ExportMenu } from './ExportMenu';
import { PrivilegedViewBanner } from '../acuity/PrivilegedViewBanner';
import { navigate, getTimezoneAbbr, resolveTimezone } from '../utils/dateUtils';
import type { ScheduleConfig, ScheduleEvent, ScheduleCallbacks, ViewType, ToolbarButtonsConfig, ConnectionStatus, ScreenerGateConfig, ProviderMatchingConfig, PatientContext, WaitlistConfig, NoShowRiskConfig, GroupTherapyConfig } from '../types';
import { BackfillNotification } from '../waitlist/BackfillNotification';
import { RiskSummaryPanel } from '../risk/RiskSummaryPanel';

// ─── Default timezone list ─────────────────────────────────────────────────

const DEFAULT_TIMEZONES: string[] = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Helsinki',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
];


function formatTimezoneOption(tz: string, date: Date): string {
  const abbr = getTimezoneAbbr(tz, date);
  const city = tz.split('/').pop()?.replace(/_/g, ' ') ?? tz;
  return city === 'UTC' ? 'UTC' : `${abbr} – ${city}`;
}

// ─── Calendar icon (used in the date picker button) ───────────────────────

function CalendarIcon() {
  return (
    <svg
      width="13" height="13" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" style={{ flexShrink: 0 }}
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

// ─── Globe icon (used in the timezone picker button) ─────────────────────

function GlobeIcon() {
  return (
    <svg
      width="12" height="12" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

const VIEW_TO_BTN_KEY: Record<ViewType, keyof ToolbarButtonsConfig> = {
  day: 'day',
  weekdays: 'workWeek',
  fullWeek: 'week',
  month: 'month',
  agenda: 'agenda',
};

// ─── Zoom icons ───────────────────────────────────────────────────────────

function ZoomInIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
      <line x1="11" y1="8" x2="11" y2="14" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  );
}

function ZoomOutIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  );
}

// ─── SSE connection indicator ─────────────────────────────────────────────

const CONNECTION_CONFIG: Record<
  NonNullable<ConnectionStatus>,
  { color: string; label: string; pulse: boolean }
> = {
  connected:    { color: '#22c55e', label: 'Live',          pulse: true },
  connecting:   { color: '#f59e0b', label: 'Connecting…',   pulse: true },
  disconnected: { color: '#94a3b8', label: 'Disconnected',  pulse: false },
  error:        { color: '#ef4444', label: 'Error',         pulse: false },
};

const PULSE_STYLE_ID = 'fulcrum-sse-pulse';

function ConnectionIndicator() {
  const { connectionStatus } = useSchedule();

  useEffect(() => {
    if (!document.getElementById(PULSE_STYLE_ID)) {
      const style = document.createElement('style');
      style.id = PULSE_STYLE_ID;
      style.textContent =
        '@keyframes fulcrum-pulse{0%,100%{opacity:1}50%{opacity:.35}}';
      document.head.appendChild(style);
    }
  }, []);

  if (!connectionStatus) return null;
  const cfg = CONNECTION_CONFIG[connectionStatus];

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11,
        color: '#475569',
        padding: '0 4px',
        userSelect: 'none',
      }}
      title={`Live updates: ${connectionStatus}`}
    >
      <span
        style={{
          display: 'inline-block',
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: cfg.color,
          flexShrink: 0,
          animation: cfg.pulse ? 'fulcrum-pulse 1.4s ease-in-out infinite' : 'none',
        }}
      />
      <span>{cfg.label}</span>
    </div>
  );
}

// ─── Timezone picker ──────────────────────────────────────────────────────

function TimezonePicker({ btnBase }: { btnBase: React.CSSProperties }) {
  const { config, setTimezone } = useSchedule();
  const { timezone, availableTimezones, showTimezone, toolbarButtons, observeDaylightSaving } = config;

  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const now = new Date();
  const tzList = availableTimezones?.length ? availableTimezones : DEFAULT_TIMEZONES;
  const resolvedTimezone = resolveTimezone(timezone, observeDaylightSaving);
  const abbr = getTimezoneAbbr(resolvedTimezone, now);
  const canSwitch = tzList.length > 1;

  const closePopup = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        btnRef.current?.contains(e.target as Node) ||
        popupRef.current?.contains(e.target as Node)
      ) return;
      closePopup();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, closePopup]);

  const tzBtnCfg = toolbarButtons?.timezone;
  if (showTimezone === false || tzBtnCfg?.hidden) return null;

  const tzLabel = tzBtnCfg?.label ?? timezone;

  const chevron = (
    <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );

  return (
    <>
      <button
        ref={btnRef}
        style={{ ...btnBase, display: 'flex', alignItems: 'center', gap: 4, color: '#475569', cursor: canSwitch ? 'pointer' : 'default' }}
        onClick={canSwitch ? () => setOpen((v) => !v) : undefined}
        title={tzLabel}
        aria-label={`Timezone: ${timezone}${canSwitch ? '. Click to change.' : ''}`}
        aria-haspopup={canSwitch ? 'listbox' : undefined}
        aria-expanded={canSwitch ? open : undefined}
      >
        <GlobeIcon />{abbr}{canSwitch && chevron}
      </button>

      {canSwitch && (
        <Popup
          anchor={btnRef.current}
          show={open}
          anchorAlign={{ horizontal: 'right', vertical: 'bottom' }}
          popupAlign={{ horizontal: 'right', vertical: 'top' }}
          style={{ zIndex: 1000 }}
        >
          <div
            ref={popupRef}
            role="listbox"
            aria-label="Select timezone"
            style={{
              background: '#fff',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              borderRadius: 8,
              overflow: 'hidden',
              border: '1px solid #e2e8f0',
              marginTop: 4,
              minWidth: 180,
              maxHeight: 320,
              overflowY: 'auto',
            }}
          >
            {tzList.map((tz) => {
              const selected = tz === timezone;
              return (
                <button
                  key={tz}
                  role="option"
                  aria-selected={selected}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '7px 12px',
                    fontSize: 12,
                    background: selected ? '#eff6ff' : 'transparent',
                    color: selected ? '#2563eb' : '#1e293b',
                    fontWeight: selected ? 600 : 400,
                    border: 'none',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => { if (!selected) (e.currentTarget as HTMLButtonElement).style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { if (!selected) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                  onClick={() => { setTimezone(tz); setOpen(false); }}
                >
                  {formatTimezoneOption(tz, now)}
                </button>
              );
            })}
          </div>
        </Popup>
      )}
    </>
  );
}

// ─── Toolbar ──────────────────────────────────────────────────────────────

function Toolbar() {
  const { config, currentDate, setCurrentDate, setView, setSlotDuration, viewRange, emit, resourcePage } = useSchedule();
  const { view, timezone, headerText, headerDateFormats, toolbarButtons, observeDaylightSaving, agendaDays, zoom, slotDuration } = config;
  const resolvedTimezone = resolveTimezone(timezone, observeDaylightSaving);

  const [showCalendar, setShowCalendar] = useState(false);
  const dateBtnRef = useRef<HTMLButtonElement>(null);
  const popupContentRef = useRef<HTMLDivElement>(null);

  // Close the calendar popup on outside clicks
  useEffect(() => {
    if (!showCalendar) return;
    const handler = (e: MouseEvent) => {
      if (
        dateBtnRef.current?.contains(e.target as Node) ||
        popupContentRef.current?.contains(e.target as Node)
      ) return;
      setShowCalendar(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showCalendar]);

  const handlePrev = () => {
    setCurrentDate(navigate(view, currentDate, 'prev', agendaDays));
    emit({ type: 'DATE_NAVIGATED', payload: viewRange });
  };
  const handleNext = () => {
    setCurrentDate(navigate(view, currentDate, 'next', agendaDays));
    emit({ type: 'DATE_NAVIGATED', payload: viewRange });
  };
  const handleToday = () => {
    setCurrentDate(new Date());
    emit({ type: 'DATE_NAVIGATED', payload: viewRange });
  };

  const handleCalendarChange = (e: CalendarChangeEvent) => {
    if (!e.value) return;
    const picked = e.value;
    // Interpret the calendar selection as midnight in the configured timezone
    const newDate = fromZonedTime(
      new Date(picked.getFullYear(), picked.getMonth(), picked.getDate(), 0, 0, 0),
      resolvedTimezone,
    );
    setCurrentDate(newDate);
    setShowCalendar(false);
    emit({ type: 'DATE_NAVIGATED', payload: viewRange });
  };

  const viewLabels: Record<string, string> = {
    day: 'Day',
    weekdays: 'Work Week',
    fullWeek: 'Week',
    month: 'Month',
    agenda: 'Agenda',
  };

  const btnBase: React.CSSProperties = {
    padding: '4px 10px',
    fontSize: 12,
    border: '1px solid #cbd5e1',
    borderRadius: 4,
    background: '#fff',
    cursor: 'pointer',
    color: '#1e293b',
  };
  const iconBtnBase: React.CSSProperties = {
    ...btnBase,
    padding: '4px 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const visibleViews = (['day', 'weekdays', 'fullWeek', 'month', 'agenda'] as const)
    .filter((v) => !toolbarButtons?.[VIEW_TO_BTN_KEY[v]]?.hidden);

  const isMobile = window.innerWidth < 768;

  // Default formats match the previous hard-coded behaviour
  const defaultFormats: Record<string, Intl.DateTimeFormatOptions> = {
    day:      { weekday: 'long',  month: 'long',  day: 'numeric', year: 'numeric' },
    weekdays: { month: 'long',  year: 'numeric' },
    fullWeek: { month: 'long',  year: 'numeric' },
    month:    { month: 'long',  year: 'numeric' },
  };
  const compactFormats: Record<string, Intl.DateTimeFormatOptions> = {
    day:      { weekday: 'short', month: 'short', day: 'numeric' },
    weekdays: { month: 'short', year: 'numeric' },
    fullWeek: { month: 'short', year: 'numeric' },
    month:    { month: 'short', year: 'numeric' },
  };

  // Agenda shows a "Jun 15 – Jul 14, 2026" range label unless the host provides
  // a custom headerDateFormats.agenda (in which case we format currentDate with it).
  let dateLabel: string;
  if (view === 'agenda' && !headerDateFormats?.agenda) {
    const windowDays = agendaDays ?? 30;
    if (windowDays === 1) {
      // Single-day agenda — range label would be "Jun 29 – Jun 29"; show a single date instead
      dateLabel = currentDate.toLocaleDateString('en-US', {
        weekday: isMobile ? 'short' : 'long',
        month: isMobile ? 'short' : 'long',
        day: 'numeric',
        ...(isMobile ? {} : { year: 'numeric' }),
        timeZone: resolvedTimezone,
      });
    } else {
      const endDate = addDays(currentDate, windowDays - 1);
      const startStr = currentDate.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', timeZone: resolvedTimezone,
      });
      const endStr = endDate.toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', timeZone: resolvedTimezone,
      });
      dateLabel = `${startStr} – ${endStr}`;
    }
  } else {
    const formatOpts = headerDateFormats?.[view as keyof typeof headerDateFormats]
      ?? (isMobile ? compactFormats[view] : defaultFormats[view])
      ?? { month: isMobile ? 'short' : 'long', year: 'numeric' };
    dateLabel = currentDate.toLocaleDateString('en-US', { ...formatOpts, timeZone: resolvedTimezone });
  }

  // Zoom controls
  const ZOOM_STEP = 5;
  const zoomMin = zoom?.min ?? 5;
  const zoomMax = zoom?.max ?? 60;
  const showZoom = zoom?.show && (view === 'day' || view === 'weekdays' || view === 'fullWeek');
  const handleZoomIn = () => setSlotDuration(Math.max(zoomMin, slotDuration - ZOOM_STEP));
  const handleZoomOut = () => setSlotDuration(Math.min(zoomMax, slotDuration + ZOOM_STEP));

  // Wall-clock value fed to the Kendo Calendar so it highlights the right date
  const calendarValue = toZonedTime(currentDate, resolvedTimezone);

  // ── TV Mode toolbar ────────────────────────────────────────────────────────
  const tvMode = config.tvMode;
  if (tvMode?.enabled) {
    const tvResources = config.resources ?? [];
    const perPage = typeof config.resourcesPerPage === 'number' ? config.resourcesPerPage : 4;
    const totalPages = Math.ceil(tvResources.length / perPage);
    const startIdx = resourcePage * perPage + 1;
    const endIdx = Math.min((resourcePage + 1) * perPage, tvResources.length);

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px 16px',
          borderBottom: '1px solid #e2e8f0',
          background: '#f8fafc',
          flexShrink: 0,
          gap: 12,
        }}
      >
        {/* Left: logo + header text */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
          {tvMode.logoUrl && (
            <img
              src={tvMode.logoUrl}
              alt=""
              style={{ height: 36, maxWidth: 140, objectFit: 'contain', flexShrink: 0 }}
            />
          )}
          {headerText && (
            <span style={{ fontWeight: 700, fontSize: 16, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {headerText}
            </span>
          )}
        </div>

        {/* Center: current date (display-only) */}
        <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', whiteSpace: 'nowrap' }}>
          {dateLabel}
        </div>

        {/* Right: resource paging indicator */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          {tvResources.length > perPage && (
            <span style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
              {`Resources ${startIdx}–${endIdx} of ${tvResources.length} • Page ${resourcePage + 1} of ${totalPages}`}
            </span>
          )}
        </div>
      </div>
    );
  }
  // ──────────────────────────────────────────────────────────────────────────

  const dateBtnStyle: React.CSSProperties = {
    padding: '4px 8px',
    fontSize: 14,
    fontWeight: 600,
    color: '#1e293b',
    background: showCalendar ? '#eff6ff' : 'transparent',
    border: `1px solid ${showCalendar ? '#bfdbfe' : 'transparent'}`,
    borderRadius: 4,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    whiteSpace: 'nowrap',
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'center',
        padding: '8px 12px',
        borderBottom: '1px solid #e2e8f0',
        background: '#f8fafc',
        flexShrink: 0,
        gap: 8,
      }}
    >
      {/* Left: optional header text + connection status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        {headerText && (
          <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {headerText}
          </span>
        )}
        <ConnectionIndicator />
      </div>

      {/* Center: navigation + clickable date */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button style={btnBase} onClick={handlePrev} aria-label="Previous">‹</button>
        <button style={btnBase} onClick={handleToday}>Today</button>
        <button style={btnBase} onClick={handleNext} aria-label="Next">›</button>

        <button
          ref={dateBtnRef}
          style={dateBtnStyle}
          onClick={() => setShowCalendar((v) => !v)}
          onKeyDown={(e) => { if (e.key === 'Escape') setShowCalendar(false); }}
          title="Click to select a date"
          aria-haspopup="dialog"
          aria-expanded={showCalendar}
          aria-label={`Current date: ${dateLabel}. Click to pick a different date.`}
        >
          <CalendarIcon />
          {dateLabel}
        </button>

        <Popup
          anchor={dateBtnRef.current}
          show={showCalendar}
          anchorAlign={{ horizontal: 'left', vertical: 'bottom' }}
          popupAlign={{ horizontal: 'left', vertical: 'top' }}
          style={{ zIndex: 1000 }}
        >
          <div
            ref={popupContentRef}
            style={{
              background: '#fff',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              borderRadius: 8,
              overflow: 'hidden',
              border: '1px solid #e2e8f0',
              marginTop: 4,
            }}
            onKeyDown={(e) => { if (e.key === 'Escape') setShowCalendar(false); }}
          >
            <Calendar
              value={calendarValue}
              onChange={handleCalendarChange}
            />
          </div>
        </Popup>
      </div>

      {/* Right: zoom + view segment + timezone + export */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
        {showZoom && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <button
              style={{ ...iconBtnBase, opacity: slotDuration >= zoomMax ? 0.4 : 1, cursor: slotDuration >= zoomMax ? 'default' : 'pointer' }}
              onClick={handleZoomOut}
              disabled={slotDuration >= zoomMax}
              title="Zoom out (larger time slots)"
              aria-label="Zoom out"
            >
              <ZoomOutIcon />
            </button>
            <span style={{ fontSize: 11, color: '#64748b', minWidth: 30, textAlign: 'center', userSelect: 'none' }}>
              {slotDuration}m
            </span>
            <button
              style={{ ...iconBtnBase, opacity: slotDuration <= zoomMin ? 0.4 : 1, cursor: slotDuration <= zoomMin ? 'default' : 'pointer' }}
              onClick={handleZoomIn}
              disabled={slotDuration <= zoomMin}
              title="Zoom in (smaller time slots)"
              aria-label="Zoom in"
            >
              <ZoomInIcon />
            </button>
          </div>
        )}
        {showZoom && visibleViews.length > 1 && (
          <div style={{ width: 1, height: 20, background: '#e2e8f0', flexShrink: 0 }} />
        )}
        {visibleViews.length > 1 && (
          <>
            <div style={{ display: 'flex' }}>
              {visibleViews.map((v, idx) => {
                const isFirst = idx === 0;
                const isLast = idx === visibleViews.length - 1;
                const isActive = view === v;
                const label = toolbarButtons?.[VIEW_TO_BTN_KEY[v]]?.label ?? viewLabels[v];
                return (
                  <button
                    key={v}
                    style={{
                      padding: '4px 10px',
                      fontSize: 12,
                      border: '1px solid',
                      borderColor: isActive ? '#2563eb' : '#cbd5e1',
                      borderLeft: isFirst ? undefined : 'none',
                      borderRadius: isFirst && isLast ? 4 : isFirst ? '4px 0 0 4px' : isLast ? '0 4px 4px 0' : 0,
                      background: isActive ? '#3B82F6' : '#fff',
                      color: isActive ? '#fff' : '#1e293b',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                    title={label}
                    aria-label={label}
                    aria-pressed={isActive}
                    onClick={() => {
                      setView(v);
                      emit({ type: 'VIEW_CHANGED', payload: { view: v, range: viewRange } });
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div style={{ width: 1, height: 20, background: '#e2e8f0', flexShrink: 0 }} />
          </>
        )}
        <TimezonePicker btnBase={btnBase} />
        {!toolbarButtons?.export?.hidden && <ExportMenu />}
      </div>
    </div>
  );
}

// ─── Inner (consumes context) ──────────────────────────────────────────────

function ScheduleCalendarInner() {
  const { config, privilegedView, waitlistHandle, noShowRiskHandle, groupTherapyHandle } = useSchedule();
  const showResourcePanel = !!config.resourcePanel && (config.resources?.length ?? 0) > 0;
  const showRightPanel = !!config.schedulingQueue || !!config.propertiesPanel || !!config.aiChatPanel || waitlistHandle.enabled || groupTherapyHandle.enabled;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {privilegedView && <PrivilegedViewBanner />}
      <Toolbar />
      {noShowRiskHandle.enabled && <RiskSummaryPanel />}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'row' }}>
        {showResourcePanel && <ResourcePanel panelConfig={config.resourcePanel!} />}
        <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <SchedulerView />
        </div>
        {showRightPanel && <RightPanel />}
      </div>
      <BackfillNotification />
    </div>
  );
}

// ─── Public component ──────────────────────────────────────────────────────

export interface ScheduleCalendarProps {
  config: ScheduleConfig;
  events?: ScheduleEvent[];
  callbacks: ScheduleCallbacks;
  /** Enable postMessage bridge for iframe embedding */
  iframeMode?: boolean;
  /** Live-update connection status. When provided, renders a status indicator in the toolbar. */
  connectionStatus?: ConnectionStatus;
  /**
   * Optional intake screener gate. When `enabled`, clicking an empty slot intercepts
   * the appointment flow and shows a screener dialog before confirming the booking.
   * Omit entirely for backward-compatible behaviour (no screener).
   */
  screenerGate?: ScreenerGateConfig;
  /**
   * When true, all reserved acuity slots are visible regardless of their
   * `visibleToGeneral` flag. A banner is shown to indicate privileged mode.
   * Defaults to false.
   */
  privilegedView?: boolean;
  /**
   * When set with `enabled: true`, activates provider-patient matching.
   * The host scores and ranks providers; the component renders filtered/ranked
   * results and surfaces matching commands through the AI chat panel.
   */
  providerMatching?: ProviderMatchingConfig;
  /**
   * Optional patient context pre-loaded on mount.
   * Fires a MATCHING_REQUEST immediately when provided.
   * Can also be pushed at any time via a PATIENT_CONTEXT_UPDATE postMessage.
   */
  patientContext?: PatientContext;
  /**
   * When provided with `enabled: true`, activates the patient waitlist and
   * auto-backfill system. Slot openings (cancellations, acuity releases) are
   * emitted to the host; the host owns all patient data and matching logic.
   */
  waitlist?: WaitlistConfig;
  /**
   * When provided with `enabled: true`, activates the no-show risk scoring and
   * smart reminder engine. The host scores each appointment and owns all delivery
   * logic; the component renders risk badges and fires timed reminder events.
   */
  noShowRisk?: NoShowRiskConfig;
  /**
   * When provided with `enabled: true`, activates the group therapy module.
   * Renders capacity badges on group events, adds a Groups roster tab to the
   * right panel, and handles the enroll / attendance / conflict-check flows.
   * The host owns all patient data, roster state, and conflict detection logic.
   */
  groupTherapy?: GroupTherapyConfig;
  style?: React.CSSProperties;
  className?: string;
}

export function ScheduleCalendar({
  config,
  events = [],
  callbacks,
  iframeMode = false,
  connectionStatus,
  screenerGate,
  privilegedView = false,
  providerMatching,
  patientContext,
  waitlist,
  noShowRisk,
  groupTherapy,
  style,
  className,
}: ScheduleCalendarProps) {
  return (
    <ScheduleProvider
      config={config}
      events={events}
      callbacks={callbacks}
      iframeMode={iframeMode}
      connectionStatus={connectionStatus}
      screenerGate={screenerGate}
      privilegedView={privilegedView}
      providerMatching={providerMatching}
      patientContext={patientContext}
      waitlist={waitlist}
      noShowRisk={noShowRisk}
      groupTherapy={groupTherapy}
    >
      <div
        className={className}
        style={{ height: '100%', display: 'flex', flexDirection: 'column', ...style }}
        data-fulcrum-schedule
      >
        <ScheduleCalendarInner />
      </div>
    </ScheduleProvider>
  );
}

// ─── Iframe wrapper (for the host side) ───────────────────────────────────

export interface ScheduleCalendarIframeProps {
  src: string;
  style?: React.CSSProperties;
  className?: string;
  title?: string;
}

export function ScheduleCalendarIframe({
  src,
  style,
  className,
  title = 'Schedule',
}: ScheduleCalendarIframeProps) {
  const ref = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    // The host can post messages directly via ref.current?.contentWindow?.postMessage(...)
  }, []);

  return (
    <iframe
      ref={ref}
      src={src}
      title={title}
      className={className}
      style={{ border: 'none', width: '100%', height: '100%', ...style }}
    />
  );
}

// Re-export for convenience
export type { ScheduleConfig };
